import { useCallback, useEffect, useState } from 'react';
import { bumpPatch, type RuleSet, type RuleVersionSummary } from '@kkp-fraud/shared';
import { ApiError, COOKIE_MODE, api, type ApiSettings } from '../api';
import { computeDiff, summariseDiff, type RuleSetDiff } from '../diff';
import { t } from '../i18n';
import { fill } from './Interpolated';
import { useLocalStorage } from '../hooks/useLocalStorage';
import type { RuleSetDraft } from '../hooks/useRuleSetDraft';
import { canPublish, type Session } from '../session';
import { isDemoVersion } from '../demo';
import { Tip } from './PageHead';
import { Dialog } from './Dialog';
import { When } from './When';
import { settingLabel, settingValues, statusLabel } from './ProposalDiffView';

/**
 * Tab 5 — the release history (§6.5 governance evidence, and the rollback the README used
 * to list as missing). Every release is an immutable snapshot of the WHOLE document, so
 * "activate" is not a pointer move: it releases that document again as a new version.
 *
 * Both auth modes roll back the same way — the decision API's activate route, live at once.
 * Cookie mode goes through the BFF (`POST /api/rules/versions/{v}/activate`), which names the
 * actor from the session, so there is no actor field; the maker-checker step is dropped for
 * now (product-owner decision). A Viewer gets no rollback button at all.
 */

function countChanges(diff: RuleSetDiff): number {
  return diff.counts.added + diff.counts.removed + diff.counts.changed + diff.meta.length;
}

/** The read-only diff of a stored version against the document the engine is running. */
function VersionDiff({ version, ruleSet, active }: { version: string; ruleSet: RuleSet; active: RuleSet | null }) {
  const diff = computeDiff(active, ruleSet);
  return (
    <section className="card" data-testid={`version-diff-${version}`}>
      <header className="card-head">
        <h2>
          <span className="mono">{version}</span> {t('versions.diff.title')}
        </h2>
        <span className={`chip ${diff.dirty ? 'warn' : 'ok'}`} data-testid="version-diff-summary">
          {diff.dirty ? summariseDiff(diff) : t('versions.diff.identical')}
        </span>
        <Tip>
          {fill(t('versions.diff.note'), {
            '{before}': <b>{t('versions.diff.before')}</b>,
            '{after}': <b>{t('versions.diff.after')}</b>,
          })}
        </Tip>
      </header>
      <div className="card-body">
        {/*
          The document note is release metadata — it describes the snapshot it was released
          with, not the draft being edited. It used to sit in an editable textarea on the
          Rule Management side panel, where it read as a standing caption on every screen.
          Here it is read-only and attached to the version it actually belongs to.
        */}
        {ruleSet.note && (
          <div className="kv-row" style={{ marginBottom: 10 }} data-testid={`version-note-${version}`}>
            <span className="k">{t('ruledoc.note.key')}</span>
            <span className="v">{ruleSet.note}</span>
          </div>
        )}

        {diff.meta.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div className="note" style={{ margin: '0 0 4px' }}>
              {t('versions.meta.title')}
            </div>
            <table>
              <tbody>
                {diff.meta.map((c, i) => {
                  const [before, after] = settingValues(c.path, c.before, c.after);
                  return (
                    <tr key={`${c.path}-${i}`}>
                      <td className="nowrap" style={{ width: 200 }}>{settingLabel(c.path)}</td>
                      <td className="mono small diff-before" style={{ width: '40%' }}>{before}</td>
                      <td className="mono small diff-after">{after}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {diff.rules.map((d) => (
          <div key={`${d.kind}-${d.id}`} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
            <div className="row-wrap">
              <span className={`chip ${d.status === 'added' ? 'approve' : d.status === 'removed' ? 'decline' : 'notify'}`}>{statusLabel(d.status)}</span>
              <span className="mono">{d.id}</span>
              <span>{d.name}</span>
              <span className="small muted">{d.kind === 'ruleBased' ? t('kind.rb.plain') : t('kind.pb.plain')}</span>
            </div>
            {d.changes.length > 0 && (
              <table style={{ marginTop: 4 }}>
                <tbody>
                  {d.changes.map((c, i) => (
                    <tr key={`${c.path}-${i}`}>
                      <td className="nowrap" style={{ width: 200 }}>{settingLabel(c.path)}</td>
                      <td className="mono small diff-before" style={{ width: '40%' }}>{settingValues(c.path, c.before, c.after)[0]}</td>
                      <td className="mono small diff-after">{settingValues(c.path, c.before, c.after)[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        {!diff.dirty && <div className="empty">{t('versions.identical.note')}</div>}
      </div>
    </section>
  );
}

export function VersionsTab({
  state,
  settings,
  session,
  onToast,
  mayPublish,
}: {
  state: RuleSetDraft;
  settings: ApiSettings;
  /** Cookie mode only — rolling back needs the Editor or Approver role. */
  session?: Session | null;
  onToast: (message: string, kind?: 'ok' | 'error') => void;
  /** R8b: `publish` on the set on screen, decided by the caller; absent = the role decides. */
  mayPublish?: boolean;
}) {
  const [versions, setVersions] = useState<RuleVersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewing, setViewing] = useState<{ version: string; ruleSet: RuleSet } | null>(null);
  const [confirming, setConfirming] = useState<{ version: string; ruleSet: RuleSet } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [actor, setActor] = useLocalStorage<string>('kkp.rule-admin.actor', '');
  const [comment, setComment] = useState('');

  const activeVersion = state.loaded?.version ?? null;
  const nextVersion = activeVersion ? bumpPatch(activeVersion) : '—';
  const mayRollBack = mayPublish ?? (!COOKIE_MODE || canPublish(session ?? null));

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVersions((await api.versions(settings)).versions);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [settings]);

  useEffect(() => {
    void refresh();
  }, [refresh, activeVersion]);

  const view = async (version: string) => {
    setBusy(version);
    setError(null);
    try {
      const res = await api.version(settings, version);
      setViewing({ version, ruleSet: res.ruleSet });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const openConfirm = async (version: string) => {
    setBusy(version);
    setError(null);
    try {
      const res = await api.version(settings, version);
      setConfirming({ version, ruleSet: res.ruleSet });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const activate = async () => {
    if (!confirming) return;
    setBusy(confirming.version);
    try {
      const res = await api.activateVersion(settings, confirming.version, actor.trim(), comment.trim() || undefined);
      setConfirming(null);
      setViewing(null);
      setComment('');
      onToast(
        typeof res.activeWithinSeconds === 'number'
          ? t('versions.toast.activated-within', { from: res.rolledBackTo, version: res.version, seconds: res.activeWithinSeconds })
          : t('versions.toast.activated', { from: res.rolledBackTo, version: res.version }),
        'ok',
      );
      // The live document moved, so the draft and the history both have to be refetched.
      await state.reload();
      await refresh();
    } catch (e) {
      const message = e instanceof ApiError ? e.message : (e as Error).message;
      setError(message);
      onToast(t('versions.toast.activateFailed', { message }), 'error');
    } finally {
      setBusy(null);
    }
  };

  const confirmDiff = confirming ? computeDiff(state.loaded, confirming.ruleSet) : null;

  return (
    <div className="stack">
      <section className="card">
        <header className="card-head">
          <h2>{t('versions.history.title')}</h2>
          <span className="chip" data-testid="versions-count">
            {versions.length === 1 ? t('versions.count.one', { n: versions.length }) : t('versions.count.other', { n: versions.length })}
          </span>
          <span className="spacer" />
          <button type="button" className="btn" onClick={() => void refresh()} disabled={loading}>
            {loading ? t('btn.loading.label') : t('btn.refresh.label')}
          </button>
          <Tip>
            {fill(t('versions.history.note'), { '{whole}': <b>{t('versions.history.whole')}</b> })}
          </Tip>
        </header>
        <div className="card-body">

          {error && (
            <div className="banner error" role="alert">
              {error}
            </div>
          )}

          {loading && versions.length === 0 && <div className="empty">{t('versions.loading.label')}</div>}

          {!loading && versions.length === 0 && !error && (
            <div className="empty">{t('versions.empty.label', { tab: t(COOKIE_MODE ? 'menu.publish.label' : 'tab.review.label') })}</div>
          )}

          {versions.length > 0 && (
            <div className="tbl-wrap">
              <table className="fixed" data-testid="versions-table">
                <colgroup>
                  <col style={{ width: 112 }} />
                  <col style={{ width: 112 }} />
                  <col style={{ width: 100 }} />
                  <col />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 244 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t('versions.col.version')}</th>
                    <th>{t('versions.col.saved')}</th>
                    <th>{t('versions.col.actor')}</th>
                    <th>{t('versions.col.comment')}</th>
                    <th className="nowrap">{t('versions.col.enabled')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.version} data-testid={`version-row-${v.version}`}>
                      <td className="mono">
                        <span className="nowrap">{v.version}</span>{' '}
                        {v.isActive && (
                          <span className="chip ok" data-testid={`active-badge-${v.version}`}>
                            {t('versions.active.label')}
                          </span>
                        )}
                      </td>
                      <td className="small nowrap">
                        <When iso={v.savedUtc} />
                      </td>
                      <td className="small">{v.actor}</td>
                      <td className="small">{v.comment ?? '—'}</td>
                      <td className="small nowrap">
                        {v.ruleBasedEnabled + v.patternBasedEnabled}{' '}
                        <span className="muted">
                          ({v.ruleBasedEnabled} {t('kind.rb.short')} · {v.patternBasedEnabled} {t('kind.pb.short')})
                        </span>
                      </td>
                      <td className="nowrap">
                        <button type="button" className="btn" onClick={() => void view(v.version)} disabled={busy !== null}>
                          {t('btn.view.label')}
                        </button>{' '}
                        {/* A Viewer may not roll back, so there is nothing to press. */}
                        {!isDemoVersion(v.version) && mayRollBack && (
                          <button
                            type="button"
                            className="btn"
                            data-testid={`activate-${v.version}`}
                            onClick={() => void openConfirm(v.version)}
                            disabled={busy !== null || v.isActive}
                            title={v.isActive ? t('versions.alreadyLive.tip') : t('versions.releaseAgain.tip', { version: nextVersion })}
                          >
                            {COOKIE_MODE ? t('dlg.ver-rollback.title') : t('versions.activate.action')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {viewing && (
        <>
          <div className="row">
            <button type="button" className="btn" onClick={() => setViewing(null)}>
              {t('versions.closeDiff.action')}
            </button>
          </div>
          <VersionDiff version={viewing.version} ruleSet={viewing.ruleSet} active={state.loaded} />
        </>
      )}

      {confirming && confirmDiff && (
        <Dialog
          title={
            COOKIE_MODE
              ? t('versions.rollback.dialogTitle', { version: confirming.version })
              : t('versions.activate.dialogTitle', { version: confirming.version })
          }
          a11yLabel={t('versions.activate.a11y', { version: confirming.version })}
          onClose={() => setConfirming(null)}
          busy={busy !== null}
          closeButton={false}
          foot={
            <>
              <span className="spacer" />
              <button type="button" className="btn" onClick={() => setConfirming(null)} disabled={busy !== null}>
                {t('btn.cancel.label')}
              </button>
              <button
                type="button"
                className="btn primary"
                data-testid="confirm-activate"
                onClick={() => void activate()}
                disabled={busy !== null || (!COOKIE_MODE && actor.trim().length === 0)}
                title={
                  !COOKIE_MODE && actor.trim().length === 0
                    ? t('versions.actorRequired.tip')
                    : t('versions.releaseAs.tip', { version: nextVersion })
                }
              >
                {busy !== null ? t('versions.releasing.label') : t('versions.releaseAs.action', { version: nextVersion })}
              </button>
            </>
          }
        >
          <div className="banner warn" data-testid="activate-explanation">
            {fill(t('versions.release.line'), {
              '{picked}': <span className="mono">{confirming.version}</span>,
              '{next}': <span className="mono">{nextVersion}</span>,
            })}{' '}
            <Tip>
            {fill(t('versions.release.note'), {
              '{whole}': <strong>{t('versions.entireRuleSet.emph')}</strong>,
              '{picked}': <span className="mono">{confirming.version}</span>,
              '{picked2}': <span className="mono">{confirming.version}</span>,
              '{next}': <span className="mono">{nextVersion}</span>,
              '{active}': <span className="mono">{activeVersion ?? '—'}</span>,
            })}
            </Tip>
          </div>

          <div className="banner small" data-testid="activate-diff-count">
            {confirmDiff.dirty
              ? countChanges(confirmDiff) === 1
                ? t('versions.changes.one', { n: countChanges(confirmDiff), summary: summariseDiff(confirmDiff) })
                : t('versions.changes.other', { n: countChanges(confirmDiff), summary: summariseDiff(confirmDiff) })
              : t('versions.noChanges.note')}
          </div>

          {!COOKIE_MODE && (
            <label className="field">
              {t('versions.actor.label')}
              <input
                type="text"
                aria-label={t('versions.actor.a11y')}
                placeholder={t('release.actor.hint')}
                value={actor}
                onChange={(e) => setActor(e.target.value)}
              />
            </label>
          )}
          <label className="field">
            <span>
              {t('versions.comment.label')} <Tip>{t('versions.record.tip', { version: confirming.version })}</Tip>
            </span>
            <textarea
              aria-label={t('versions.comment.a11y')}
              placeholder={t('versions.comment.placeholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </label>
        </Dialog>
      )}
    </div>
  );
}
